import RefreshIcon from "@assets/icons/refresh.svg";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { AppConfigApis } from "@services/apis/AppConfig";
import React, { useCallback, useMemo, useState } from "react";

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

function extractPayload(resp: any): Json {
  if (!resp) return null;
  const d = "data" in resp ? resp.data : resp;
  if (d && typeof d === "object") {
    if ("conf" in d) return (d as any).conf;
    if ("data" in d) return (d as any).data;
  }
  return d as Json;
}

function stableStringify(value: Json, space = 2): string {
  const seen = new WeakSet();
  const stringify = (v: Json): any => {
    if (v && typeof v === "object") {
      if (seen.has(v as object)) return "[Circular]";
      seen.add(v as object);
      if (Array.isArray(v)) return v.map(stringify);
      const obj = v as Record<string, Json>;
      const keys = Object.keys(obj).sort();
      const out: Record<string, any> = {};
      for (const k of keys) out[k] = stringify(obj[k]);
      return out;
    }
    return v;
  };
  return JSON.stringify(stringify(value), null, space);
}

function parseJsonWithLocation(text: string): {
  value?: Json;
  error?: { message: string; line?: number; column?: number };
} {
  try {
    return { value: JSON.parse(text) as Json };
  } catch (e: any) {
    const msg: string = e?.message || "Invalid JSON";
    const match = /position\s+(\d+)/i.exec(msg);
    if (match) {
      const pos = Number(match[1]);
      const before = text.slice(0, pos);
      const lines = before.split(/\r?\n/);
      const line = lines.length;
      const column = lines[lines.length - 1].length + 1;
      return { error: { message: msg, line, column } };
    }
    return { error: { message: msg } };
  }
}

const MobileAppConfig: React.FC = () => {
  const [originalJson, setOriginalJson] = useState<Json>(null);
  const [editorText, setEditorText] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const parsed = useMemo(() => parseJsonWithLocation(editorText), [editorText]);
  const isValid = !!parsed.value && !parsed.error;

  const isDirty = useMemo(() => {
    if (!isValid) return false;
    const a = stableStringify(originalJson ?? null, 0);
    const b = stableStringify(parsed.value!, 0);
    return a !== b;
  }, [isValid, originalJson, parsed]);

  const getConfig = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      setSaveMsg(null);
      const resp = await AppConfigApis.GetApplicationConfig();
      const payload = extractPayload(resp);
      setOriginalJson(payload);
      setEditorText(stableStringify(payload, 2));
      setHasLoaded(true);
    } catch (err: any) {
      console.error("Get config failed:", err);
      setFetchError(err?.message || "Failed to fetch JSON.");
    } finally {
      setLoading(false);
    }
  }, []);

  const setConfig = useCallback(async () => {
    if (!isValid) return;
    try {
      setLoading(true);
      setFetchError(null);
      setSaveMsg(null);
      await AppConfigApis.SetApplicationConfig(parsed.value);
      setOriginalJson(parsed.value!);
      setEditorText(stableStringify(parsed.value!, 2));
      setSaveMsg("Configuration updated successfully.");
    } catch (err: any) {
      console.error("Save config failed:", err);
      setFetchError(err?.message || "Failed to save JSON.");
    } finally {
      setLoading(false);
    }
  }, [isValid, parsed]);

  const resetEdits = useCallback(() => {
    setEditorText(stableStringify(originalJson ?? null, 2));
    setSaveMsg(null);
    setFetchError(null);
  }, [originalJson]);

  const format = useCallback(() => {
    const p = parseJsonWithLocation(editorText);
    if (p.value !== undefined) setEditorText(stableStringify(p.value, 2));
  }, [editorText]);

  const minify = useCallback(() => {
    const p = parseJsonWithLocation(editorText);
    if (p.value !== undefined) setEditorText(stableStringify(p.value, 0));
  }, [editorText]);

  return (
    <div className="flex flex-col h-full">
      <div className="h-[56px] border-b border-border-subtle bg-white flex items-center px-4">
        <p className="text-[14px] font-semibold">App-config (Generic JSON)</p>
      </div>

      <div className="p-3 flex flex-col gap-3 flex-1 overflow-auto">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={getConfig}
            className="flex items-center border-border-subtle border-[1px] rounded-md overflow-hidden disabled:opacity-50"
            disabled={loading}
          >
            <span className="px-3 py-2 text-[13px]">
              {loading ? "Loading..." : "Get Config"}
            </span>
            <span className="p-2 bg-bg-button-secondary rounded-lg m-1">
              <img src={RefreshIcon} alt="" />
            </span>
          </button>

          <LightCustomButton
            onClick={setConfig}
            text={loading ? "Updating..." : "Update"}
            className={`px-4 py-2 text-white rounded disabled:opacity-50 ${
              isDirty && isValid ? "bg-blue-600" : "bg-gray-400"
            }`}
            isActive={isDirty && isValid && !loading}
          />

          <button
            onClick={resetEdits}
            className="px-3 py-2 text-[13px] border-[1px] border-border-subtle rounded disabled:opacity-50"
            disabled={!hasLoaded || loading || !isDirty}
          >
            Reset
          </button>

          <button
            onClick={format}
            className="px-3 py-2 text-[13px] border-[1px] border-border-subtle rounded disabled:opacity-50"
            disabled={!hasLoaded || loading}
            title="Pretty print JSON"
          >
            Format
          </button>

          <button
            onClick={minify}
            className="px-3 py-2 text-[13px] border-[1px] border-border-subtle rounded disabled:opacity-50"
            disabled={!hasLoaded || loading}
            title="Minify JSON"
          >
            Minify
          </button>
        </div>

        {fetchError && (
          <div className="text-red-600 text-[12px] whitespace-pre-wrap">
            {fetchError}
          </div>
        )}
        {saveMsg && (
          <div className="text-green-600 text-[12px]">{saveMsg}</div>
        )}

        <div className="flex flex-col gap-2 flex-1">
          {!hasLoaded ? (
            <p className="text-sm text-gray-600">
              Click "Get Config" to load and edit arbitrary JSON.
            </p>
          ) : (
            <>
              <textarea
                value={editorText}
                onChange={(e) => setEditorText(e.target.value)}
                spellCheck={false}
                className="w-full flex-1 min-h-[300px] border-[1px] border-border-subtle rounded p-3 font-mono text-[13px] leading-5"
                placeholder='Type valid JSON, e.g. { "foo": "bar", "nested": { "a": 1 } }'
              />

              {!isValid && parsed.error && (
                <div className="text-red-600 text-[12px]">
                  {parsed.error.line
                    ? `JSON error at line ${parsed.error.line}, column ${parsed.error.column}: ${parsed.error.message}`
                    : `JSON error: ${parsed.error.message}`}
                </div>
              )}

              {isValid && (
                <div className="text-[12px] text-gray-600">
                  {isDirty ? "Unsaved changes." : "No changes."} JSON is valid.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileAppConfig;
